import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  Result,
  MathDiv,
  ActionButtonsRow,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function SteelBending() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fy, setFy] = useState(345)
  const [e, setE] = useState(200000)
  const [ly, setLy] = useState(0)
  const [cb, setCb] = useState(1.14)
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [profileName, setProfileName] = useState('W150X13,0')
  const [profiles, setProfiles] = useState([])
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  useEffect(() => {
    async function fetchProfiles() {
      const response = await api.get('/wprofiles')
      setProfiles([...response.data.profiles])
    }

    fetchProfiles()
  }, [])

  const handleProfileChange = (event) => {
    const selectedProfile = event.target.value

    setProfileName(selectedProfile)
  }

  const HISTORY_KEY = 'steel-bending-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFy(entry.fy)
      setE(entry.e)
      setLy(entry.ly)
      setCb(entry.cb)
      setProfileName(entry.profileName)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (name !== 'rhoMin' && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fy = validateField('fy', fy)
    newErrors.e = validateField('e', e)
    newErrors.ly = validateField('ly', ly)
    newErrors.cb = validateField('cb', cb)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fy, e, ly, cb, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fy,
        e,
        profileName,
        ly,
        cb,
      }

      const response = await api.post('/steel/bending', data)

      setData(data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fy,
        e,
        profileName,
        ly,
        cb,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fy,
      e,
      profileName,
      ly,
      cb,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFy(jsonData.fy)
        setE(jsonData.e)
        setProfileName(jsonData.profileName)
        setLy(jsonData.ly)
        setCb(jsonData.cb)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o arquivo. Verifique o formato.')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
      errorMessage: 'Erro ao copiar os cálculos. Tente novamente.',
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Flexão</span>
          </div>
          <h1>🔩 Flexão em Perfis de Aço</h1>
          <p className="subtitle">Dimensionamento e verificação de perfis de aço submetidos à flexão simples</p>
          <div className="badges">
            <Badge>📋 ABNT NBR 8800:2008</Badge>
            <Badge>🧮 Flambagem Local e Lateral</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de perfis de aço submetidos à flexão simples, conforme a ABNT
              NBR 8800:2008.
            </p>
            <p>
              O cálculo considera os efeitos de flambagem lateral com torção (FLT), flambagem local da mesa (FLM) e
              flambagem local da alma (FLA), determinando o momento fletor resistente do perfil selecionado.
            </p>
            <div className="norms">
              <span>ABNT NBR 8800:2008</span>
              <span>Perfis Laminados</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <h3>Materiais</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Tensão de escoamento do perfil (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy || ''}
                onChange={(event) => setFy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Módulo de elasticidade (MPa)</span>
              <ValidatedInput
                name="e"
                type="number"
                value={e || ''}
                onChange={(event) => setE(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            {profiles.length > 0 && (
              <InputDiv>
                <span>Perfil</span>
                <div>
                  <Select
                    style={{ marginLeft: '0' }}
                    id="profileSelect"
                    value={profileName}
                    onChange={handleProfileChange}
                  >
                    {profiles.map((profile, index) => (
                      <option key={index} value={profile.name}>
                        {profile.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </InputDiv>
            )}
            <InputDiv>
              <span>Comprimento destravado (cm)</span>
              <ValidatedInput
                name="ly"
                type="number"
                value={ly || ''}
                onChange={(event) => setLy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Outros dados</h3>
            <InputDiv>
              <span>Coeficiente Cb</span>
              <ValidatedInput
                name="cb"
                type="number"
                value={cb || ''}
                onChange={(event) => setCb(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <h3>Verificação de FLT</h3>
                {result.flt.mrd.case === 1 && (
                  <BlockMath math={`M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.flt.mrd.case === 2 && (
                  <BlockMath math={`M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.flt.mrd.case === 3 && (
                  <BlockMath math={`M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                <h3>Verificação de FLM</h3>
                {result.flm.mrd.case === 1 && (
                  <BlockMath math={`M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.flm.mrd.case === 2 && (
                  <BlockMath math={`M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.flm.mrd.case === 3 && (
                  <BlockMath math={`M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                <h3>Verificação de FLA</h3>
                {result.fla.mrd.case === 1 && (
                  <BlockMath math={`M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.fla.mrd.case === 2 && (
                  <BlockMath math={`M_{Rd} = ${result.fla.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                {result.fla.mrd.case === 3 && (
                  <BlockMath math={`M_{Rd} = ${result.fla.mrd.mrd.toFixed(2)} \\text{ kN.cm}`} />
                )}
                <h3>Momento resistente</h3>
                <BlockMath math={`M_{Rd} = ${result.mrd.toFixed(2)} \\text{ kN.cm}`} />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <MathDiv>
                <BlockMath math={`M_{pl} = Z_x \\cdot f_y`} />
                <BlockMath
                  math={`M_{pl} = ${result.profile.zx} \\cdot ${data.fy / 10} = ${result.mpl} \\text{ kN.cm}`}
                />
                <BlockMath math={`M_{r} = 0.7 \\cdot f_y \\cdot W_x`} />
                <BlockMath math={`M_{r} = 0.7 \\cdot ${data.fy / 10} \\cdot ${result.profile.wx} `} />
                <BlockMath math={`M_{r} = ${result.mr.toFixed(2)} \\text{ kN.cm}`} />
                <h3>Verificação de FLT</h3>
                <BlockMath math={`\\lambda = \\dfrac{l_b}{r_y}`} />
                <BlockMath
                  math={`\\lambda = \\dfrac{${data.ly}}{${result.profile.ry}} = ${result.flt.lambda.toFixed(2)}`}
                />
                <BlockMath math={`\\lambda_p = 1.76 \\cdot \\left( \\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                <BlockMath
                  math={`\\lambda_p = 1.76 \\cdot \\left( \\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.flt.lambdaP.toFixed(2)}`}
                />
                <BlockMath
                  math={`\\lambda_r = \\dfrac{1.38 \\cdot \\left( I_y \\cdot J \\right)^{\\dfrac{1}{2}} }{r_y \\cdot J \\cdot \\beta_1}`}
                />
                <BlockMath math={`\\beta_1 = \\dfrac{0.7 \\cdot f_y \\cdot W_x}{E \\cdot J}`} />
                <BlockMath
                  math={`\\beta_1 = \\dfrac{0.7 \\cdot ${data.fy} \\cdot ${result.profile.wx} }{ ${data.e} \\cdot ${result.profile.it}} = ${result.flt.beta1.toFixed(2)}`}
                />
                <BlockMath
                  math={`\\lambda_r = \\dfrac{1.38 \\cdot \\left( ${result.profile.iy} \\cdot ${result.profile.it} \\right)^{\\dfrac{1}{2}} }{${result.profile.ry} \\cdot ${result.profile.it} \\cdot ${result.flt.beta1.toFixed(2)}}`}
                />
                <BlockMath math={`\\lambda_r = ${result.flt.lambdaR.toFixed(2)}`} />
                {result.flt.mrd.case === 1 && (
                  <>
                    <BlockMath math={`\\lambda \\leq \\lambda_p`} />
                    <BlockMath
                      math={`M_{Rd} = \\dfrac{M_{pl}}{\\gamma_{a1}} = \\dfrac{${result.mpl}}{${result.gammaa1} }`}
                    />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.flt.mrd.case === 2 && (
                  <>
                    <BlockMath math={`\\lambda_p < \\lambda \\leq \\lambda_r`} />
                    <BlockMath
                      math={`M_{Rd} = A \\cdot \\left[M_{pl} - B \\right] \\leq \\dfrac{M_{pl}}{\\gamma_{a1}}`}
                    />
                    <BlockMath math={`A = \\dfrac{C_b}{\\gamma_{a1}} = \\dfrac{${data.cb}}{${result.gammaa1}}`} />
                    <BlockMath
                      math={`B = \\left( M_{pl} - M_r \\right) \\cdot \\dfrac{\\lambda - \\lambda_p}{\\lambda_r - \\lambda_p}`}
                    />
                    <BlockMath
                      math={`B = \\left( ${result.mpl} - ${result.mr.toFixed(1)} \\right) \\cdot \\dfrac{${result.flt.lambda.toFixed(1)} - ${result.flt.lambdaP.toFixed(1)}}{${result.flt.lambdaR.toFixed(1)} - ${result.flt.lambdaP.toFixed(1)}}`}
                    />
                    <BlockMath math={`M_{Rd} = \\dfrac{C_b}{\\gamma_{a1}} \\cdot \\left[M_{pl}  \\right]`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.flt.mrd.case === 3 && (
                  <>
                    <BlockMath math={`\\lambda > \\lambda_r`} />
                    <BlockMath math={`M_{cr} = \\dfrac{C_b \\cdot \\pi^2 \\cdot E \\cdot I_y}{ {L_b}^2 } \\cdot `} />
                    <BlockMath
                      math={`\\cdots \\left[ \\dfrac{C_w}{I_y} \\cdot \\left( 1 + 0.039 \\cdot \\dfrac{J \\cdot {L_b}^2}{C_w} \\right) \\right]^{\\dfrac{1}{2}}`}
                    />
                    <BlockMath
                      math={`M_{cr} = \\dfrac{${data.cb}  \\cdot \\pi^2 \\cdot ${data.e / 10} \\cdot ${result.profile.iy}}{ {${data.ly}}^2 } \\cdot `}
                    />
                    <BlockMath
                      math={`\\cdots \\left[ \\dfrac{${result.flt.mrd.cw.toFixed(1)} }{${result.profile.iy}} \\cdot \\left( 1 + 0.039 \\cdot \\dfrac{ ${result.profile.it} \\cdot ${data.ly}^2}{${result.flt.mrd.cw.toFixed(1)}} \\right) \\right]^{\\dfrac{1}{2}}`}
                    />
                    <BlockMath math={`M_{Rd} = \\dfrac{M_{cr}}{\\gamma_{a1}} \\leq \\dfrac{M_{pl}}{\\gamma_{a1}}`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flt.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                <h3>Verificação de FLM</h3>
                <BlockMath
                  math={`\\lambda = \\dfrac{b}{2 \\cdot t} = \\dfrac{${result.profile.bf}}{2 \\cdot ${result.profile.tf}}`}
                />
                <BlockMath math={`\\lambda = ${result.flm.lambda.toFixed(2)}`} />
                <BlockMath math={`\\lambda_p = 0.38 \\cdot \\left( \\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                <BlockMath
                  math={`\\lambda_p = 0.38 \\cdot \\left( \\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.flm.lambdaP.toFixed(2)}`}
                />
                <BlockMath
                  math={`\\lambda_r = 0.83 \\cdot \\left( \\dfrac{E}{0.7 \\cdot f_y} \\right)^{\\dfrac{1}{2}}`}
                />
                <BlockMath
                  math={`\\lambda_r = 0.83 \\cdot \\left( \\dfrac{${data.e}}{0.7 \\cdot ${data.fy}} \\right)^{\\dfrac{1}{2}} = ${result.flm.lambdaR.toFixed(2)}`}
                />
                {result.flm.mrd.case === 1 && (
                  <>
                    <BlockMath math={`\\lambda \\leq \\lambda_p`} />
                    <BlockMath
                      math={`M_{Rd} = \\dfrac{M_{pl}}{\\gamma_{a1}} = \\dfrac{${result.mpl}}{${result.gammaa1} }`}
                    />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.flm.mrd.case === 2 && (
                  <>
                    <BlockMath math={`\\lambda_p < \\lambda \\leq \\lambda_r`} />
                    <BlockMath
                      math={`M_{Rd} = A \\cdot \\left[M_{pl} - B \\right] \\leq \\dfrac{M_{pl}}{\\gamma_{a1}}`}
                    />
                    <BlockMath math={`A = \\dfrac{C_b}{\\gamma_{a1}} = \\dfrac{${data.cb}}{${result.gammaa1}}`} />
                    <BlockMath
                      math={`B = \\left( M_{pl} - M_r \\right) \\cdot \\dfrac{\\lambda - \\lambda_p}{\\lambda_r - \\lambda_p}`}
                    />
                    <BlockMath
                      math={`B = \\left( ${result.mpl} - ${result.mr.toFixed(1)} \\right) \\cdot \\dfrac{${result.flm.lambda.toFixed(1)} - ${result.flm.lambdaP.toFixed(1)}}{${result.flm.lambdaR.toFixed(1)} - ${result.flm.lambdaP.toFixed(1)}}`}
                    />
                    <BlockMath math={`M_{Rd} = \\dfrac{C_b}{\\gamma_{a1}} \\cdot \\left[M_{pl}  \\right]`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.flm.mrd.case === 3 && (
                  <>
                    <BlockMath math={`\\lambda > \\lambda_r`} />
                    <BlockMath math={`M_{cr} = \\dfrac{0.69 \\cdot E}{\\lambda^2} \\cdot W_c`} />
                    <BlockMath
                      math={`M_{cr} = \\dfrac{0.69 \\cdot ${data.e / 10}}{${result.flm.lambdaR}^2} \\cdot ${result.profile.wx}`}
                    />
                    <BlockMath math={`M_{Rd} = \\dfrac{M_{cr}}{\\gamma_{a1}} \\leq \\dfrac{M_{pl}}{\\gamma_{a1}}`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                <h3>Verificação de FLA</h3>
                <BlockMath math={`\\lambda = \\dfrac{h}{t_w} = \\dfrac{${result.profile.dl}}{${result.profile.tw}}`} />
                <BlockMath math={`\\lambda = ${result.fla.lambda.toFixed(2)}`} />
                <BlockMath math={`\\lambda_p = 3.74 \\cdot \\left( \\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                <BlockMath
                  math={`\\lambda_p = 3.74 \\cdot \\left( \\dfrac{${data.e}}{${data.fy} } \\right)^{\\dfrac{1}{2}} = ${result.fla.lambdaP.toFixed(2)}`}
                />
                <BlockMath math={`\\lambda_p = 5.7 \\cdot \\left( \\dfrac{E}{f_y} \\right)^{\\dfrac{1}{2}}`} />
                <BlockMath
                  math={`\\lambda_p = 5.7 \\cdot \\left( \\dfrac{${data.e}}{${data.fy}} \\right)^{\\dfrac{1}{2}} = ${result.fla.lambdaR.toFixed(2)}`}
                />
                {result.fla.mrd.case === 1 && (
                  <>
                    <BlockMath math={`\\lambda \\leq \\lambda_p`} />
                    <BlockMath
                      math={`M_{Rd} = \\dfrac{M_{pl}}{\\gamma_{a1}} = \\dfrac{${result.mpl}}{${result.gammaa1} }`}
                    />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.flm.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.fla.mrd.case === 2 && (
                  <>
                    <BlockMath math={`\\lambda_p < \\lambda \\leq \\lambda_r`} />
                    <BlockMath
                      math={`M_{Rd} = A \\cdot \\left[M_{pl} - B \\right] \\leq \\dfrac{M_{pl}}{\\gamma_{a1}}`}
                    />
                    <BlockMath math={`A = \\dfrac{C_b}{\\gamma_{a1}} = \\dfrac{${data.cb}}{${result.gammaa1}}`} />
                    <BlockMath
                      math={`B = \\left( M_{pl} - M_r \\right) \\cdot \\dfrac{\\lambda - \\lambda_p}{\\lambda_r - \\lambda_p}`}
                    />
                    <BlockMath
                      math={`B = \\left( ${result.mpl} - ${result.mr.toFixed(1)} \\right) \\cdot \\dfrac{${result.fla.lambda.toFixed(1)} - ${result.fla.lambdaP.toFixed(1)}}{${result.fla.lambdaR.toFixed(1)} - ${result.fla.lambdaP.toFixed(1)}}`}
                    />
                    <BlockMath math={`M_{Rd} = \\dfrac{C_b}{\\gamma_{a1}} \\cdot \\left[M_{pl}  \\right]`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.fla.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                {result.fla.mrd.case === 3 && (
                  <>
                    <BlockMath math={`\\lambda > \\lambda_r`} />
                    <BlockMath math={`\\boxed{M_{Rd} = ${result.fla.mrd.mrd.toFixed(2)} \\text{ kN.cm}}`} />
                  </>
                )}
                <h3>Momento resistente</h3>
                <BlockMath math={`M_{Rd} = \\text{menor} \\left( M_{FLT}, M_{FLA}, M_{FLM} \\right)`} />
                <BlockMath math={`\\boxed{M_{Rd} = ${result.mrd.toFixed(2)} \\text{ kN.cm}}`} />
              </MathDiv>
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
